import { Component, ViewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  PoBreadcrumb,
  PoNotificationService,
  PoUploadComponent,
  PoUploadFileRestrictions
} from '@po-ui/ng-components';
import { Router } from '@angular/router';
import { DpLancamentoRubricasService } from './dp-lancamento-rubricas.service';

type ArquivoUpload = { name: string; rawFile?: File };

@Component({
  selector: 'app-dp-lancamento-rubricas',
  templateUrl: './dp-lancamento-rubricas.component.html',
  styleUrls: ['./dp-lancamento-rubricas.component.css'],
  standalone: false
})
export class DpLancamentoRubricasComponent {
  @ViewChild('uploadEsocial') uploadEsocial?: PoUploadComponent;

  codiEmp: number | null = null;
  somenteRescisoes = false;
  aPartirDe = '';
  enviando = false;
  avisos: string[] = [];

  readonly urlPlaceholder = 'https://po-ui.io';
  readonly esocialRestrictions: PoUploadFileRestrictions = {
    allowedExtensions: ['.xml', '.zip'],
    maxFileSize: 180 * 1024 * 1024
  };

  readonly breadcrumb: PoBreadcrumb = {
    items: [
      { label: 'Home', link: '/home' },
      { label: 'DP' },
      { label: 'Lançamento de rubricas' }
    ]
  };

  constructor(
    private readonly lancamentoService: DpLancamentoRubricasService,
    private readonly poNotificationService: PoNotificationService,
    private readonly router: Router
  ) {}

  irParaCadastro(): void {
    void this.router.navigate(['/dp-rubricas']);
  }

  lancar(): void {
    if (this.enviando) return;

    if (!this.codiEmp || this.codiEmp <= 0) {
      this.poNotificationService.warning('Selecione a empresa.');
      return;
    }

    const data = this.aPartirDe.trim();
    if (data && !/^\d{2}\/\d{2}\/\d{4}$/.test(data)) {
      this.poNotificationService.warning('Use a data no formato dd/mm/aaaa. Em branco, vale 01/12/2025.');
      return;
    }

    const arquivos = this.arquivos();
    if (!arquivos.length) {
      this.poNotificationService.warning('Envie os XMLs ou o .zip do eSocial.');
      return;
    }

    this.enviando = true;
    this.avisos = [];
    this.lancamentoService.lancar(this.codiEmp, arquivos, this.somenteRescisoes, data).subscribe({
      next: response => {
        this.enviando = false;
        const blob = response.body;
        if (!blob || blob.size === 0) {
          this.poNotificationService.error('A planilha do lançamento voltou vazia.');
          return;
        }

        const avisos = this.avisosDoCabecalho(response.headers.get('x-ecco-avisos'));
        this.mostrarAvisos(avisos);
        this.baixar(
          blob,
          this.nomeArquivo(response.headers.get('content-disposition'), this.codiEmp!, this.somenteRescisoes)
        );
        this.poNotificationService.success(
          avisos.length
            ? 'Planilha baixada. Veja os avisos nesta tela e a última aba da planilha.'
            : 'Lançamento enviado. A planilha mostra o que foi para a Domínio.'
        );
        this.uploadEsocial?.clear();
      },
      error: err => {
        this.enviando = false;
        void this.lerErro(err).then(resultado => {
          this.mostrarAvisos(resultado.avisos);
          if (resultado.erro && !resultado.avisos.includes(resultado.erro)) {
            this.poNotificationService.error(resultado.erro);
            this.avisos = [resultado.erro, ...this.avisos];
          } else if (!resultado.avisos.length) {
            this.poNotificationService.error('Não foi possível lançar.');
          }
        });
      }
    });
  }

  private mostrarAvisos(lista: string[]): void {
    this.avisos = lista;
    for (const aviso of lista) {
      if (this.ehAvisoDeCadastro(aviso)) this.poNotificationService.warning(aviso);
      else this.poNotificationService.error(aviso);
    }
  }

  private ehAvisoDeCadastro(texto: string): boolean {
    const normalizado = texto.toLowerCase();
    return normalizado.includes('cadastrada na ecco')
      || normalizado.includes('sem rubricas cadastradas')
      || normalizado.includes('nenhuma rubrica');
  }

  private arquivos(): File[] {
    const files = (this.uploadEsocial?.currentFiles ?? []) as ArquivoUpload[];
    return files.map(item => item.rawFile).filter((file): file is File => !!file);
  }

  private baixar(blob: Blob, nome: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = nome;
    link.click();
    URL.revokeObjectURL(url);
  }

  private nomeArquivo(contentDisposition: string | null, codiEmp: number, somenteRescisoes: boolean): string {
    const match = /filename\*?=(?:UTF-8''|"?)([^";]+)/i.exec(contentDisposition ?? '');
    if (!match) {
      const modo = somenteRescisoes ? 'rescisoes' : 'rubricas';
      return `lancamento_${modo}_${codiEmp}.xlsx`;
    }
    return decodeURIComponent(match[1].replace(/"/g, ''));
  }

  private avisosDoCabecalho(valor: string | null): string[] {
    if (!valor) return [];
    try {
      const json = new TextDecoder().decode(Uint8Array.from(atob(valor), char => char.charCodeAt(0)));
      const lista = JSON.parse(json) as unknown;
      return Array.isArray(lista) ? lista.filter((item): item is string => typeof item === 'string' && !!item.trim()) : [];
    } catch {
      return [];
    }
  }

  private async lerErro(err: HttpErrorResponse): Promise<{ erro: string; avisos: string[] }> {
    const body = err?.error;
    if (body instanceof Blob) {
      const texto = await body.text();
      try {
        const json = JSON.parse(texto) as { error?: string; Error?: string; avisos?: string[] };
        return {
          erro: json.error || json.Error || texto || 'Não foi possível lançar.',
          avisos: (json.avisos ?? []).filter(item => !!item?.trim())
        };
      } catch {
        return { erro: texto || 'Não foi possível lançar.', avisos: [] };
      }
    }
    if (typeof body === 'string' && body.trim()) return { erro: body, avisos: [] };
    if (body && typeof body === 'object') {
      const json = body as { error?: string; Error?: string; title?: string; avisos?: string[] };
      return {
        erro: json.error || json.Error || json.title || 'Não foi possível lançar.',
        avisos: (json.avisos ?? []).filter(item => !!item?.trim())
      };
    }
    return { erro: 'Não foi possível lançar.', avisos: [] };
  }
}
